import { useEffect, useRef, useCallback } from 'react';

interface UsePollingOptions {
  /** Polling interval in milliseconds (default: 30000 = 30 seconds) */
  interval?: number;
  /** Whether polling is enabled (default: true) */
  enabled?: boolean;
  /** Whether to poll immediately when enabled/visible (default: true) */
  pollImmediately?: boolean;
}

/**
 * A visibility-aware polling hook that pauses when the tab is hidden
 * and resumes (with an immediate poll) when the tab becomes visible.
 *
 * @param pollFn - The async function to call on each poll. Should handle ETag-based
 *                 304 responses internally (return early if data unchanged).
 * @param options - Polling configuration options
 */
export function usePolling(
  pollFn: () => Promise<void>,
  options: UsePollingOptions = {}
): void {
  const {
    interval = 30000,
    enabled = true,
    pollImmediately = true
  } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef(!document.hidden);

  // Memoize the poll function to avoid unnecessary effect triggers
  const stablePollFn = useCallback(pollFn, [pollFn]);

  // Start polling
  const startPolling = useCallback(() => {
    if (intervalRef.current) return; // Already polling

    intervalRef.current = setInterval(() => {
      stablePollFn().catch(err => {
        console.error('[Polling] Error:', err);
      });
    }, interval);
  }, [stablePollFn, interval]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Handle visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      const wasVisible = isVisibleRef.current;
      const isNowVisible = !document.hidden;
      isVisibleRef.current = isNowVisible;

      if (!enabled) return;

      if (isNowVisible && !wasVisible) {
        // Tab became visible - trigger immediate poll and restart interval
        stablePollFn().catch(err => {
          console.error('[Polling] Error on visibility resume:', err);
        });
        startPolling();
      } else if (!isNowVisible && wasVisible) {
        // Tab became hidden - stop polling to save resources
        stopPolling();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, stablePollFn, startPolling, stopPolling]);

  // Main polling effect
  useEffect(() => {
    if (!enabled) {
      stopPolling();
      return;
    }

    // Only poll if tab is visible
    if (document.hidden) {
      return;
    }

    // Immediate poll on mount/enable if requested
    if (pollImmediately) {
      stablePollFn().catch(err => {
        console.error('[Polling] Error on initial poll:', err);
      });
    }

    startPolling();

    return () => {
      stopPolling();
    };
  }, [enabled, pollImmediately, stablePollFn, startPolling, stopPolling]);
}
